import React, { useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useQuery } from '@tanstack/react-query';
import { fetchMonthlyReport } from '@/lib/monthlyReportUtils';
import { format, subMonths, addMonths } from 'date-fns';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { MetaTags } from '@/components/MetaTags';
import { MonthlyReportCard } from '@/components/analytics/MonthlyReportCard';

export default function MonthlyReport() {
  const { user } = useAuth();
  const [selectedDate, setSelectedDate] = useState(() => subMonths(new Date(), 1));

  const { data: stats, isLoading } = useQuery({
    queryKey: ['monthly-report', user?.id, format(selectedDate, 'yyyy-MM')],
    queryFn: () => fetchMonthlyReport(user!.id, selectedDate),
    enabled: !!user,
  });

  const goToPreviousMonth = () => setSelectedDate(prev => subMonths(prev, 1));
  const goToNextMonth = () => {
    const next = addMonths(selectedDate, 1);
    if (next <= new Date()) setSelectedDate(next);
  };

  const canGoForward = addMonths(selectedDate, 1) <= new Date();

  if (!user) return null;

  return (
    <div className="min-h-screen pb-24">
      <MetaTags title="Monthly Report" description="Your monthly reading stats" />

      {/* Month selector */}
      <div className="flex items-center justify-center gap-4 py-4 sticky top-0 z-10 bg-background/80 backdrop-blur-md">
        <Button variant="ghost" size="icon" onClick={goToPreviousMonth}>
          <ChevronLeft className="w-5 h-5" />
        </Button>
        <h2 className="text-lg font-heading font-bold min-w-[180px] text-center">
          {format(selectedDate, 'MMMM yyyy')}
        </h2>
        <Button variant="ghost" size="icon" onClick={goToNextMonth} disabled={!canGoForward}>
          <ChevronRight className="w-5 h-5" />
        </Button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" />
        </div>
      ) : !stats || stats.totalBooksFinished === 0 ? (
        <div className="text-center py-16 px-4">
          <img src="/dino-logo.png" alt="Dino" className="w-20 h-20 mx-auto mb-4 opacity-50" />
          <h3 className="text-lg font-heading font-bold text-muted-foreground">No books finished this month</h3>
          <p className="text-sm text-muted-foreground mt-2">Keep reading — your next report is in the making! 🦕</p>
        </div>
      ) : (
        <div className="px-3 max-w-lg mx-auto">
          <MonthlyReportCard stats={stats} selectedDate={selectedDate} />
        </div>
      )}
    </div>
  );
}
